const translations = {
  PageTitle: "Lidar Clearance",
  FailedToLoad: "Failed to load Lidar Clearance data",

  SystemsTab: "Rule Systems",
  AxesTab: "Axes",
  ColorScalesTab: "Color Scales",
  RulesTab: "Rules",

  NewSystemButton: "New System",
  NameColumn: "Name",
  AxesColumn: "Axes",

  CreateSystemDialogTitle: "New Clearance System",
  RegionLabel: "Region",
  AllRegionsOption: "ANY",
  NameLabel: "Name",
  CreateButton: "Create",
  CancelButton: "Cancel",
  SaveButton: "Save",
  FailedToCreateSystem: "Failed to create system",
  FailedToUpdateSystem: "Failed to update system",

  LinkedAxesTitle: "Linked Axes",
  LinkedAxesNameColumn: "Axis Name",
  LinkedAxesColorScaleColumn: "Color Scale",
  AxisLabel: "Axis",
  AxisOptionLabel: (name: string, id: number) => `${name} (ID: ${id})`,
  ColorScaleDefaultOption: "Default",
  ColorScaleGapWarning: (axisValueCount: number, scaleStopCount: number) =>
    `This axis has ${axisValueCount} possible values but the color scale only defines ${scaleStopCount} - some values won't have a color.`,
  AddAxisButton: "Add",
  RemoveAxisLink: "Remove",
  NoAxesLinked: "No axes linked yet.",
  FailedToLinkAxis: "Failed to link axis",
  FailedToUpdateAxisLink: "Failed to update axis link",
  FailedToUnlinkAxis: "Failed to unlink axis",

  OpenLabelsButton: "Edit labels",
  LabelsTitle: "Labels",
  NewLabelButton: "New Label",
  FillCombinationsButton: "Fill Combinations",
  FillAxisButton: "Fill",
  NoLabelsYet: "No labels yet.",
  NoIndividualLabelsForAxis: "No individual labels yet.",
  LabelCodeLabel: "Code",
  LabelNameLabel: "Name",
  LabelAxisValuesColumn: "Axes",
  LabelAxisValuesHint: "Axes left at 0 aren't stored - they already default to 0 for any label that doesn't mention them.",
  ResultingCriticalityLabel: "Resulting criticality",
  AllZeroCombination: "(all axes at 0)",
  CombinedLabelsSummary: (count: number) =>
    `${count} combined label${count === 1 ? "" : "s"}`,
  RemoveLabel: "Remove",
  EditLabelName: "Edit",
  CreateLabelDialogTitle: "New Label",
  EditLabelDialogTitle: "Edit Label",
  FillCombinationsDialogTitle: "Fill Combinations",
  FillButton: "Fill",
  FillCombinationCountHint: (count: number) =>
    `This will generate up to ${count} label${count === 1 ? "" : "s"}, skipping any combination that already has one.`,
  FillCombinationsOnlyHint: "Pick an axis, then choose which axes to combine it with.",
  CombineWithLabel: "Combine with",
  SelectAxisFirstHint: "Select an axis to combine.",
  ExistingCombinationsLabel: "Existing combinations",
  FailedToLoadLabels: "Failed to load labels",
  FailedToSaveLabel: "Failed to save label",
  FailedToDeleteLabel: "Failed to delete label",
  FailedToFillLabels: "Failed to fill labels",
  CopyLabelsButton: "Import labels",
  CopyLabelsDialogTitle: "Copy labels from another system",
  CopyFromSystemLabel: "Copy from system",
  NoOtherSystemsWithAxis: "No other system in this region links this axis.",
  CopyButton: "Copy",
  FailedToCopyLabels: "Failed to copy labels",
  NewAxisButton: "New Axis",
  IdColumn: "ID",
  RangeColumn: "Range",
  ColorScaleColumn: "Default Color Scale",

  CreateAxisDialogTitle: "New Clearance Axis",
  NameEnLabel: "Name (English)",
  NameVisibleToCustomersNote: "This name is visible to customers.",
  ValueRangeLabel: "Value Range",
  ValueRangeHint: "The ordinal scale (0-255) this axis's raw measurements are mapped to upstream - not a physical unit. Thresholds and the colour scale below only apply within this range.",
  MinValueLabel: "Min",
  MinValueLockedHint: "0 is treated as not present. An axis must always include 0.",
  MaxValueLabel: "Max",
  ColorScaleLabel: "Default color scale",
  ColorScaleHint: "Colours each value in this range for the point-cloud viewer. A system can override this for one of its linked axes from the Systems tab.",
  ThresholdsLabel: "Thresholds",
  ThresholdsDescription: "Determines what severity each axis value range converts to.",
  SeverityCriticalLabel: "Critical",
  SeverityIssueLabel: "Issue",
  SeverityShouldBeFixedLabel: "Should be fixed",
  SeverityGoodToKnowLabel: "Good to know",
  SeverityNoIssueLabel: "No issue",
  SeverityRangeLabel: (lower: number, upper: number, severity: string) =>
    `${lower} – ${upper} falls into ${severity}`,
  SeverityRangeInvalid: (severity: string) =>
    `No values fall into ${severity} - thresholds must decrease from 1 to 4`,
  SeverityRangeSuffix: (upper: number, severity: string) =>
    `– ${upper} falls into ${severity}`,
  SeverityRangeInvalidSuffix: (severity: string) =>
    `– no values fall into ${severity} (thresholds must decrease from 1 to 4)`,
  FailedToCreateAxis: "Failed to create axis",
  FailedToUpdateAxis: "Failed to update axis",

  NewColorScaleButton: "New Color Scale",
  StopsColumn: "Stops",
  PreviewColumn: "Preview",

  CreateColorScaleDialogTitle: "New Color Scale",
  AddStopButton: "Add stop",
  RemoveStop: "Remove",
  FailedToCreateColorScale: "Failed to create color scale",
  FailedToUpdateColorScale: "Failed to update color scale",
  UsedAsDefaultColorTitle: (count: number) => `Used as default color (${count})`,
  NoAxesUseColorScale: "No axes use this color scale yet.",
  UsedInSystemsTitle: (count: number) => `Used in systems (${count})`,
  NoSystemsUseColorScale: "No systems override an axis to this color scale.",
  AxisColumn: "Axis",
  SystemColumn: "System",

  FilterByIdOrNamePlaceholder: "Filter by ID or name",
  FilterByIdPlaceholder: "Filter by ID",
  NoMatchingRows: "No rows match your filter",

  RulesTabIntro: "Build the clearance rules file ds-lidar-pipeline reads for a system - add a rule, define the conditions that trigger it, and which axes it sets when they hold.",
  SelectSystemLabel: "System",
  NoLinkedAxesHint: "This system has no linked axes yet - link one from the Systems tab first.",
  RulesSectionDescription:
    "Rules are evaluated independently for every point. When more than one rule matches & sets the same axis, the highest value is kept (a rule can only raise a severity, never lower one it).",
  SkippedInvalidConfigEntries: (count: number) =>
    `${count} entr${count === 1 ? "y" : "ies"} in this system's saved config couldn't be read and ${count === 1 ? "was" : "were"} skipped - check the JSON below against what's expected.`,
  NoRulesYetHint: "No rules yet - add one to define what triggers a severity.",
  AddRuleButton: "Add rule",
  RemoveRuleButton: "Remove rule",
  RuleTitlePlaceholder: "New rule (no axis set)",
  IncompleteRuleWarning: "This rule has no action set, or an action is missing its severity, so it won't do anything.",
  ExpandAllButton: "Expand all",
  CollapseAllButton: "Collapse all",
  DuplicateRuleButton: "Duplicate rule",
  MoveRuleUpButton: "Move rule up",
  MoveRuleDownButton: "Move rule down",
  ConditionsLabel: "Conditions",
  AddConditionButton: "Add condition",
  AddGroupButton: "Add group",
  RemoveConditionButton: "Remove",
  CombinatorAllLabel: "All must hold (AND)",
  CombinatorAnyLabel: "Any may hold (OR)",
  ActionsLabel: "Sets",
  SeverityValueLabel: "Severity",
  VariableLabel: "Variable",
  VariablePowerlineDistance: "Distance to Powerline",
  VariablePowerlineDistanceHorizontal: "Distance to Powerline (Horizontal)",
  VariablePowerlineDistanceVertical: "Distance to Powerline (Vertical)",
  VariableHeightAboveGround: "Height above ground",
  VariablePositionAlongSpan: "Position along span",
  VariableSpanLength: "Span length",
  VariableCentreLineDistance: "Distance from center powerline",
  VariableCentreLineDistanceHorizontal: "Distance from center powerline (Horizontal)",
  VariableCentreLineDistanceVertical: "Distance from center powerline (Vertical)",
  VariableOuterPhaseDistanceHorizontal: "Distance from outer phase (Horizontal)",
  VariableClassification: "Classification",
  OperatorLabel: "Operator",
  ValueLabel: "Value",
  OperatorLessThan: "Less than",
  OperatorLessThanOrEqualTo: "Less than or equal to",
  OperatorGreaterThan: "Greater than",
  OperatorGreaterThanOrEqualTo: "Greater than or equal to",
  OperatorEqualTo: "Equal to",
  JsonPreviewTitle: "Generated clearance config",
  SaveConfigButton: "Save",
  FailedToSaveConfig: "Failed to save clearance config",
  SavedConfigMessage: "Clearance config saved",

  EntityChecksTitle: "Entity Checks",
  EntityChecksHint: "Entity checks score a whole entity (e.g. a tree) instead of individual points.",
  NoEntityChecksYetHint: "No entity checks yet - add one to score whole entities (e.g. trees) rather than individual points.",
  AddEntityCheckButton: "Add entity check",
  RemoveEntityCheckButton: "Remove entity check",
  TreeRiskCheckLabel: "Tree risk",
  IsolatedAxisLabel: "Isolated axis (optional)",
  SameAsAxisOption: "Same as axis",
  RiskTreeTierLabel: "Risk Tree",
  RiskTreeTierHint: "Any tree which could hit the line.",
  LoneRiskTreeTierLabel: "Lone Risk Tree",
  LoneRiskTreeTierHint: "A tree which could hit the line and also lacks any supporting vegetation.",
  RiskTierLevelLabel: "Entity Label",
  RiskTierLevelHint: "Applied to the whole entity.",
  RiskTierPartWillHitLevelLabel: "Will-Hit-Powerline Label (Optional)",
  RiskTierPartWillHitLevelHint: "Applied to the parts of an entity predicted to hit the powerline.",
  SameAsLevelOption: "Same as entity label",

  CopyFromSystemButton: "Copy from system...",
  CopyRulesConfigDialogTitle: "Copy Clearance Config",
  CopyRulesFromSystemLabel: "Copy from",
  NoOtherSystemsWithConfig: "No other system in this region has a saved config yet.",

  ParametersTitle: "Parameters",
  ParametersHint: "Named values a condition or severity can reference instead of a literal number (e.g. a distance threshold reused across many rules) - editing one updates every rule that references it.",
  MetricParametersTitle: "Distance parameters",
  NonMetricParametersTitle: "Other parameters",
  ParameterNameLabel: "Name",
  ParameterValueLabel: "Value",
  AddParameterButton: "Add parameter",
  RemoveParameterButton: "Remove parameter",
  DuplicateParameterNameError: "Another parameter already uses this name",
  DuplicateParameterNamesBlockSave: "Resolve duplicate parameter names before saving.",
  LiteralValueOption: "Value",
  ParameterValueOption: "Parameter",

  ConfirmSkippedSaveTitle: "Save with skipped entries?",
  ConfirmSkippedSaveBody: (count: number) =>
    `This system's loaded config had ${count} entr${count === 1 ? "y" : "ies"} that couldn't be read, and ${count === 1 ? "was" : "were"} dropped from this editor. Saving now will permanently remove ${count === 1 ? "it" : "them"} from the stored config.`,
  ConfirmSkippedSaveConfirm: "Save anyway",
  ConfirmSkippedSaveCancel: "Cancel",
};

export default translations;
